import { UserDegree } from '../../../shared/models/user-degree.model';
import { UserSelfEmployed } from '../../../shared/models/user-self-employed.model';
import { User } from '../../../shared/models/user.model';
import { SlugifyPipe } from '../../../shared/pipes/slugify.pipe';
import { StudentsForImpactService } from '../../../shared/services/students-for-impact.service';
import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormGroupDirective, FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BsLocaleService, defineLocale } from 'ngx-bootstrap';
import { frLocale } from 'ngx-bootstrap/locale';

@Component({
  selector: 'app-students-for-impact-validate',
  templateUrl: './students-for-impact-validate.component.html',
  styleUrls: ['./students-for-impact-validate.component.css']
})
export class StudentsForImpactValidateComponent implements OnInit {

  @Input() user: User;
  @ViewChild('tab1', { static: false }) tab1: ElementRef;
  @ViewChild('tab2', { static: false }) tab2: ElementRef;

  modalTitle = 'Validation des documents Students for Impact';
  studentForm: FormGroup;
  selfEmployedForm: FormGroup;

  showButtonNext = true;
  showButtonPrevious = false;
  showButtonSave = false;

  // Validation status for each tab
  tab1Validated = false;
  tab2Validated = false;

  // Button click tracking
  degreeValidated: boolean = null; // null = not clicked, true = validated, false = rejected
  selfEmployedValidated: boolean = null;

  // Temporary validation data (not saved to user object until final submit)
  private degreeValidationData: {
    status: number;
    status_message: string;
    validity: Date;
  } = null;

  private selfEmployedValidationData: {
    status: number;
    status_message: string;
  } = null;

  private tabCurrent = 0;
  minDate = new Date();

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private service: StudentsForImpactService,
    private bsLocaleService: BsLocaleService
  ) {
    defineLocale(frLocale.abbr, frLocale);
    this.bsLocaleService.use('fr');
  }

  ngOnInit() {
    this.initForms();
    this.loadUserData();
  }

  initForms() {
    // Form for student status (tab 1)
    this.studentForm = this.formBuilder.group({
      validity: [null],
      status_message: ['']
    });

    // Form for self-employed status (tab 2)
    this.selfEmployedForm = this.formBuilder.group({
      status_message: ['']
    });
  }

  loadUserData() {
    if (this.user) {
      // Load existing data if available
      if (this.user.degree) {
        this.studentForm.patchValue({
          validity: this.user.degree.validity,
          status_message: this.user.degree.status_message || ''
        });
      }
      if (this.user.self_employed) {
        this.selfEmployedForm.patchValue({
          status_message: this.user.self_employed.status_message || ''
        });
      }
    }
  }

  // Format birth_date to JJ/MM/AAAA
  getFormattedBirthDate(): string {
    if (!this.user || !this.user.birth_date) return '';
    const date = new Date(this.user.birth_date);
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Get full address string
  getFullAddress(): string {
    if (!this.user || !this.user.user_adresses) return '';
    const addr = this.user.user_adresses;
    return `${addr.address_line_1 || ''}, ${addr.post_code || ''} ${addr.city || ''}, ${addr.state || ''}`;
  }

  // Check if document is PDF or image
  isPdf(url: string): boolean {
    return url && url.toLowerCase().includes('.pdf');
  }

  // Tab 1 - Validate student status
  onValidateDegree() {
    const validity = this.studentForm.get('validity').value;

    // Check that validity date is in the future
    if (!validity) {
      alert('La date d\'expiration est obligatoire pour valider le statut étudiant.');
      return;
    }

    const validityDate = new Date(validity);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (validityDate <= today) {
      alert('La date d\'expiration doit être supérieure à la date du jour.');
      return;
    }

    this.degreeValidated = true;
    this.tab1Validated = true;

    // Store validation data temporarily (not applied to user object yet)
    this.degreeValidationData = {
      status: 2, // Validated
      status_message: '',
      validity: validity
    };
  }

  // Tab 1 - Reject student status
  onRejectDegree() {
    const statusReason = this.studentForm.get('status_message').value;

    if (!statusReason || statusReason.trim() === '') {
      alert('La raison du rejet est obligatoire pour rejeter le statut étudiant.');
      return;
    }

    this.degreeValidated = false;
    this.tab1Validated = true;

    // Store validation data temporarily (not applied to user object yet)
    this.degreeValidationData = {
      status: 3, // Rejected
      status_message: statusReason,
      validity: null
    };
  }

  // Tab 2 - Validate self-employed status
  onValidateSelfEmployed() {
    this.selfEmployedValidated = true;
    this.tab2Validated = true;

    // Store validation data temporarily (not applied to user object yet)
    this.selfEmployedValidationData = {
      status: 2, // Validated
      status_message: ''
    };
  }

  // Tab 2 - Reject self-employed status
  onRejectSelfEmployed() {
    const statusReason = this.selfEmployedForm.get('status_message').value;

    if (!statusReason || statusReason.trim() === '') {
      alert('La raison du rejet est obligatoire pour rejeter le statut auto-entrepreneur.');
      return;
    }

    this.selfEmployedValidated = false;
    this.tab2Validated = true;

    // Store validation data temporarily (not applied to user object yet)
    this.selfEmployedValidationData = {
      status: 3, // Rejected
      status_message: statusReason
    };
  }

  onNext() {
    if (!this.tab1Validated) {
      alert('Vous devez valider ou rejeter le statut étudiant avant de continuer.');
      return;
    }

    this.tabCurrent++;
    this.onNavigate();
  }

  onBack() {
    this.tabCurrent--;
    this.onNavigate();
  }

  onSave() {
    if (!this.tab2Validated) {
      alert('Vous devez valider ou rejeter le statut auto-entrepreneur avant d\'enregistrer.');
      return;
    }

    // Prepare data for API from temporary validation data
    const validationData = {
      userId: this.user.id,
      degree: {
        status: this.degreeValidationData.status,
        status_message: this.degreeValidationData.status_message || null,
        validity: this.degreeValidationData.status === 2 ? this.degreeValidationData.validity : null
      },
      self_employed: {
        status: this.selfEmployedValidationData.status,
        status_message: this.selfEmployedValidationData.status_message || null
      }
    };

    // Call API
    this.service.validateImpactDocuments(this.user.id, validationData).subscribe(
      response => {
        // Only update user object on successful API call
        if (!this.user.degree) {
          this.user.degree = new UserDegree();
        }
        this.user.degree.status = this.degreeValidationData.status;
        this.user.degree.status_message = this.degreeValidationData.status_message;
        this.user.degree.validity = this.degreeValidationData.validity;

        if (!this.user.self_employed) {
          this.user.self_employed = new UserSelfEmployed();
        }
        this.user.self_employed.status = this.selfEmployedValidationData.status;
        this.user.self_employed.status_message = this.selfEmployedValidationData.status_message;

        this.activeModal.close('validation-success');
      },
      error => {
        console.error('Error validating documents:', error);
        alert('Erreur lors de l\'enregistrement de la validation.');
      }
    );
  }

  onCancel() {
    // Show confirmation if user has made changes
    if (this.tab1Validated || this.tab2Validated ||
      this.studentForm.dirty || this.selfEmployedForm.dirty) {
      if (confirm('Attention : toutes les données saisies seront perdues. Êtes-vous sûr de vouloir annuler ?')) {
        this.activeModal.dismiss('cancel');
      }
    } else {
      this.activeModal.dismiss('cancel');
    }
  }

  onNavigate() {
    switch (this.tabCurrent) {
      case 0:
        this.modalTitle = 'Validation du statut Étudiant';
        this.showButtonPrevious = false;
        this.showButtonNext = true;
        this.showButtonSave = false;
        this.selectTab(this.tab1);
        break;
      case 1:
        this.modalTitle = 'Validation du statut Auto-entrepreneur';
        this.showButtonPrevious = true;
        this.showButtonNext = false;
        this.showButtonSave = true;
        this.selectTab(this.tab2);
        break;
      default:
        this.modalTitle = 'Validation du statut Étudiant';
        this.showButtonPrevious = false;
        this.showButtonNext = true;
        this.showButtonSave = false;
        this.selectTab(this.tab1);
        break;
    }
  }

  selectTab(tab: ElementRef): void {
    const tabs = [this.tab1, this.tab2];
    tabs.forEach(tabItem => {
      if (tabItem) {
        tabItem.nativeElement.classList.remove('active');
      }
    });

    if (tab) {
      tab.nativeElement.classList.add('active');
    }
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }
}
